import { dateV2Service } from '../../../../../domain/value-object/DateV2'

export type SprintInterval = '1w' | '2w' | '1month'

export type SprintSuffix =
  | { type: 'date' }
  | { type: 'serial'; start: number; digits: number }

export type GeneratedSprint = {
  // 未指定の場合はサーバー側でランダム採番される
  code?: string
  name: string
  startDate: string
  endDate: string
}

export type NamingRule = {
  prefix: string
  suffix: SprintSuffix
}

export type LabelParams = {
  name: NamingRule
  // プレフィックス未指定のときはサーバー採番に任せるため undefined
  code?: NamingRule
}

type BaseParams = LabelParams & {
  count: number
  // 既存プレビューの末尾に追加するとき、連番を続きから振るための開始位置
  serialOffset?: number
}

type WeekParams = BaseParams & {
  interval: '1w' | '2w'
  startDate: string // YYYY/MM/DD
}

type MonthParams = BaseParams & {
  interval: '1month'
  startDayOfMonth: number // 1-31
  startDate: string // YYYY/MM/DD
}

export type GenerateSprintsParams = WeekParams | MonthParams

const DISPLAY_FORMAT = 'YYYY/MM/DD'
const NAME_DATE_FORMAT = 'YYMMDD'

const parseDate = (dateStr: string): Date => {
  const [y, m, d] = dateStr.split('/').map(Number)
  return new Date(y, m - 1, d)
}

const formatDisplay = (date: Date): string =>
  dateV2Service.format(date, DISPLAY_FORMAT)

const formatName = (date: Date): string =>
  dateV2Service.format(date, NAME_DATE_FORMAT)

const buildValue = (
  { prefix, suffix }: NamingRule,
  index: number,
  startDate: Date
): string => {
  const value =
    suffix.type === 'date'
      ? formatName(startDate)
      : String(suffix.start + index).padStart(suffix.digits, '0')
  return `${prefix}${value}`
}

const buildLabels = (
  { name, code }: LabelParams,
  index: number,
  startDate: Date
): Pick<GeneratedSprint, 'code' | 'name'> => ({
  code: code ? buildValue(code, index, startDate) : undefined,
  name: buildValue(name, index, startDate),
})

const getMonthStartDate = (
  year: number,
  month: number,
  targetDay: number
): Date => {
  const maxDay = dateV2Service.daysInMonth(year, month)
  const day = Math.min(targetDay, maxDay)
  return new Date(year, month, day)
}

const isWeekParams = (params: GenerateSprintsParams): params is WeekParams =>
  params.interval === '1w' || params.interval === '2w'

// 命名設定だけが変わったときは、手で調整した日付を保ったまま採番し直す
export const relabelSprints = (
  sprints: GeneratedSprint[],
  labels: LabelParams
): GeneratedSprint[] =>
  sprints.map((sprint, index) => ({
    ...sprint,
    ...buildLabels(labels, index, parseDate(sprint.startDate)),
  }))

// 連番は行の位置に紐づくため、日付をずらしても採番し直さない
const relabelByDate = (
  sprint: GeneratedSprint,
  { name, code }: LabelParams,
  index: number,
  newStart: Date
): Pick<GeneratedSprint, 'code' | 'name'> => ({
  name:
    name.suffix.type === 'date'
      ? buildValue(name, index, newStart)
      : sprint.name,
  code:
    code && code.suffix.type === 'date'
      ? buildValue(code, index, newStart)
      : sprint.code,
})

export const shiftSprintDates = (
  sprints: GeneratedSprint[],
  fromIndex: number,
  days: number,
  labels: LabelParams
): GeneratedSprint[] => {
  // 押した行の終了日が開始日より前にならないかチェック
  const target = sprints[fromIndex]
  const targetStart = parseDate(target.startDate)
  const targetNewEnd = dateV2Service.addDays(parseDate(target.endDate), days)
  if (dateV2Service.isBefore(targetNewEnd, targetStart)) return sprints

  return sprints.map((sprint, i) => {
    if (i < fromIndex) return sprint
    if (i === fromIndex) {
      return {
        ...sprint,
        endDate: formatDisplay(targetNewEnd),
      }
    }
    // それ以降: 開始日・終了日ともにシフト
    const newStart = dateV2Service.addDays(parseDate(sprint.startDate), days)
    const newEnd = dateV2Service.addDays(parseDate(sprint.endDate), days)
    return {
      ...relabelByDate(sprint, labels, i, newStart),
      startDate: formatDisplay(newStart),
      endDate: formatDisplay(newEnd),
    }
  })
}

export const appendSprints = (
  existing: GeneratedSprint[],
  count: number,
  interval: SprintInterval,
  labels: LabelParams,
  // '1month'のときの月次の刻み。月末で丸められる前の元の基準日を渡す。
  // 追加時に丸められた日を基準日として引き継ぐと、短い月を経由したあと縮んだまま戻らない
  startDayOfMonth: number
): GeneratedSprint[] => {
  if (count <= 0 || existing.length === 0) return existing
  const last = existing[existing.length - 1]
  const lastEnd = parseDate(last.endDate)
  const nextStart = dateV2Service.addDays(lastEnd, 1)

  const base = { ...labels, count, serialOffset: existing.length }
  const params: GenerateSprintsParams =
    interval === '1month'
      ? {
          ...base,
          interval,
          startDayOfMonth,
          startDate: formatDisplay(nextStart),
        }
      : {
          ...base,
          interval,
          startDate: formatDisplay(nextStart),
        }
  return [...existing, ...generateSprints(params)]
}

export const generateSprints = (
  params: GenerateSprintsParams
): GeneratedSprint[] => {
  const { count } = params
  if (count <= 0) return []

  const serialOffset = params.serialOffset ?? 0
  const sprints: GeneratedSprint[] = []

  if (isWeekParams(params)) {
    const durationDays = params.interval === '1w' ? 7 : 14
    let currentStart = parseDate(params.startDate)

    for (let i = 0; i < count; i++) {
      const endDate = dateV2Service.addDays(currentStart, durationDays - 1)
      sprints.push({
        ...buildLabels(params, serialOffset + i, currentStart),
        startDate: formatDisplay(currentStart),
        endDate: formatDisplay(endDate),
      })
      currentStart = dateV2Service.addDays(endDate, 1)
    }
  } else {
    const baseDate = parseDate(params.startDate)
    let currentYear = baseDate.getFullYear()
    let currentMonth = baseDate.getMonth()
    const targetDay = params.startDayOfMonth

    for (let i = 0; i < count; i++) {
      // 1件目は渡された開始日そのまま。startDayOfMonth を1件目にも当てると、
      // appendSprints が渡す「前行の終了日の翌日」を上書きしてしまい、
      // 手で日付を調整したあとに追加した行が前行と重複する
      const currentStart =
        i === 0
          ? baseDate
          : getMonthStartDate(currentYear, currentMonth, targetDay)
      let nextMonth = currentMonth + 1
      let nextYear = currentYear
      if (nextMonth > 11) {
        nextMonth = 0
        nextYear++
      }
      const nextStart = getMonthStartDate(nextYear, nextMonth, targetDay)
      const endDate = dateV2Service.addDays(nextStart, -1)

      sprints.push({
        ...buildLabels(params, serialOffset + i, currentStart),
        startDate: formatDisplay(currentStart),
        endDate: formatDisplay(endDate),
      })

      currentYear = nextYear
      currentMonth = nextMonth
    }
  }

  return sprints
}
